/**
 * AlertHexagonPage (shared) — draw.io-style pan/zoom canvas where every camera is a
 * hexagon, clustered by site → group. Colour encodes live state (grey=online,
 * dim=offline, red/amber/blue = critical/warning/normal alerts). Click a hex → floating
 * live preview beside it; hover an alerting hex → its alert data; a docked panel stacks
 * a live tile per active-alerting camera.
 *
 * Node-agnostic: data + real-time arrive via the injected `api` (HexAlertsApi, whose
 * adapter owns socket-vs-poll); the per-camera video player is the injected
 * PlayerComponent (local ULLFUPlayer / cloud LiveStreamPlayer). Cloud passes requireNode +
 * nodeKey for node-scoped lifecycle.
 *
 * All of the realtime state — counters, the live/recording debounce, tile promotion, card
 * pagination, ack/resolve — lives in `useAlertBoard`, shared with CameraMapPage. This file
 * is the hexagon LAYOUT and nothing else.
 *
 * Not routed to directly: AlertBoardPage is what the apps mount, and it renders this or one
 * of the spatial boards depending on the view toggle in its title bar. The card, its floating
 * controls and the filter panel are BoardChrome's, shared with the other two boards.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  MinusIcon,
  PlusIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';
import HexCanvas, { type HexCanvasHandle, type Viewport } from '../components/hexagon/HexCanvas';
import HexSiteView from '../components/hexagon/HexSiteView';
import type { FilterGroup } from '../components/hexagon/HexFilterBar';
import { useBoardFilters } from '../hooks/useBoardFilters';
import { useFilterColumn } from '../components/layout/FilterColumn';
import {
  BOARD_FLOAT,
  BoardCard,
  BoardFilterPanel,
  BoardFilterPill,
  BoardFullscreenButton,
  BoardIdentityChip,
  BoardToolButton,
  BoardToolGroup,
  BoardTopBar,
  camsLabel,
  type BoardStats,
} from '../components/hexagon/BoardChrome';
import ClickPreviewCard from '../components/hexagon/ClickPreviewCard';
import BoardLegend from '../components/hexagon/BoardLegend';
import HexAlertPopover from '../components/hexagon/HexAlertPopover';
import AlertTileStack from '../components/hexagon/AlertTileStack';
import {
  HEX_W,
  layoutSites,
  type PositionedHex,
  type SiteInput,
} from '../components/hexagon/hexLayout';
import {
  viewAlertTarget,
  type HexPlayerComponent,
  type ViewAlertRequest,
  type ViewAlertTarget,
} from '../components/hexagon/types';
import { useAlertBoard } from '../hooks/useAlertBoard';
import type { HexAlert, HexAlertsApi, HexCamera } from '../api/contracts';

const UNGROUPED = '__ungrouped__';
/** Panel id for a board with no site level — one background for the whole honeycomb. */
const BOARD_PANEL = '__board__';
/** The "no restriction" chip key, for every single-choice filter group. */
const ALL = '__all__';
/**
 * Where the board starts, and where "Fit" returns it to. A measured width-fit lands near
 * 100%, which shows fewer rows than operators want at a glance; 69% is the level they
 * settled on, so it is the one both the initial render and Fit are pinned to.
 */
const DEFAULT_ZOOM = 0.69;
/** Groups on the board before "Jump to group" is offered — below this they're all a glance
 *  away and the control would be clutter. */
const JUMP_MIN_GROUPS = 6;
/**
 * Empty canvas-space bands above and below the honeycomb. The canvas runs edge to edge under
 * floating controls, so without them the first group would start UNDER the Filters pill and the
 * last row would end under the legend. In content px — ~60 screen px at the default zoom.
 */
const TOP_INSET = 84;
const BOTTOM_INSET = 60;


export interface AlertHexagonPageProps {
  api: HexAlertsApi;
  PlayerComponent: HexPlayerComponent;
  /** Cloud: require a selected node; show an empty state when nodeKey is falsy. */
  requireNode?: boolean;
  /** Cloud: changes when the selected node changes → reload cameras + re-watch. */
  nodeKey?: string;
  className?: string;
  /** Optional: when set, alert tiles show a "View alert" link that calls this (each app
   *  routes to its own alert lifecycle page). */
  onViewAlert?: (alert: ViewAlertRequest) => void;
  /** Floated at the top centre of the card. AlertBoardPage puts the view toggle here while
   *  full screen, where its title bar is covered. */
  centerSlot?: ReactNode;
  /** The page is full screen — the full-screen button shows its "exit" state. */
  fullscreen?: boolean;
  /** Omit to drop the full-screen button. */
  onToggleFullscreen?: () => void;
  /** Reports open alerts / live cameras for the page's title-bar pill. */
  onStats?: (stats: BoardStats) => void;
  /** Jump to a camera's recorded footage from the live preview. Omit when the caller lacks
   *  `recordings.view` — that is what hides the button. */
  onOpenRecordings?: (cameraId: string) => void;
  /**
   * This board covers ONE site — draw every group on a single shared background, with no
   * per-site panels and no site filter.
   *
   * The local node is one place by definition, but its camera groups are a tree and the
   * board derives a "site" from each group's ROOT. Where an installation keeps its groups
   * flat, that made every group its own site: a panel per group, each captioned with the
   * group's own name twice over. Local passes this; cloud leaves it off, where sites are
   * genuinely different places. A board whose cameras show no site level above their groups
   * is treated this way regardless — there is nothing to frame.
   */
  singleSite?: boolean;
}

export default function AlertHexagonPage({
  api,
  PlayerComponent,
  requireNode = false,
  nodeKey,
  className = 'p-3 sm:p-4',
  onViewAlert,
  centerSlot,
  fullscreen = false,
  onToggleFullscreen,
  onOpenRecordings,
  singleSite = false,
  onStats,
}: AlertHexagonPageProps) {
  const blocked = requireNode && !nodeKey;

  // The hexagon shows EVERY camera, so no camera filter.
  const board = useAlertBoard({ api, blocked, nodeKey });
  const {
    cameras,
    loading,
    counters,
    recent,
    cards,
    liveTiles,
    primaryCameraId,
    heldCameraIds,
    warmupCameraId,
    nameById,
    liveFor,
    recordingFor,
    colorStateFor,
    counterFor,
    latestAlertFor,
  } = board;

  const canvasRef = useRef<HexCanvasHandle>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [vp, setVp] = useState<Viewport>({ tx: 0, ty: 0, scale: 1 });
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const onViewportChange = useCallback((v: Viewport) => setVp(v), []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // ── Filters ───────────────────────────────────────────────────────────────
  // SITE narrows what is LAID OUT (a site you aren't watching shouldn't cost canvas);
  // ALERT TYPE, SEVERITY and "only open" only dim, so the honeycomb an operator has learned
  // never reflows under them. See CameraHex's `dimmed`.
  //
  // There WAS a third group here, "Camera state" (All / Alerting / Offline). It was removed:
  // the board already says both of those things louder than a chip row can — an alerting
  // camera pulses red and carries a counter, an offline one is grey — so the filter restated
  // the colour an operator was already looking at, and "Alerting" in particular duplicated
  // "All alerts" in the group beside it. The alert-type filter is the one that answers a
  // question the board cannot: WHICH alert.
  const [siteFilter, setSiteFilter] = useState<string>(ALL);

  /** A camera's site panel name. Cameras in no group belong to no site — they are collected
   *  under one honest "Ungrouped" panel rather than invented into a site. */
  const siteNameOf = useCallback(
    (cam: HexCamera) =>
      (cam.siteId || UNGROUPED) === UNGROUPED ? 'Ungrouped' : cam.siteName || 'Site',
    [],
  );

  /** Do these cameras actually sit under a site ABOVE their group, or is "site" just the
   *  group looked at from one level up? Only the former is worth a panel each. */
  const hasSiteLevel = useMemo(
    () => cameras.some((c) => c.siteId && c.siteId !== UNGROUPED && c.siteId !== c.groupId),
    [cameras],
  );
  const mergeSites = singleSite || !hasSiteLevel;

  const siteOptions = useMemo(() => {
    const m = new Map<string, { id: string; name: string; count: number }>();
    cameras.forEach((cam) => {
      const id = cam.siteId || UNGROUPED;
      const hit = m.get(id);
      if (hit) hit.count += 1;
      else m.set(id, { id, name: siteNameOf(cam), count: 1 });
    });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [cameras, siteNameOf]);

  // A site that disappears (node switch, camera moved) must not leave the board empty with
  // no visible reason — fall back to everything.
  useEffect(() => {
    if (siteFilter !== ALL && !siteOptions.some((s) => s.id === siteFilter)) setSiteFilter(ALL);
  }, [siteOptions, siteFilter]);

  // With one site there is nothing to filter BY, so any remembered choice is inert.
  const activeSiteFilter = mergeSites ? ALL : siteFilter;

  const scopedCameras = useMemo(
    () =>
      activeSiteFilter === ALL
        ? cameras
        : cameras.filter((c) => (c.siteId || UNGROUPED) === activeSiteFilter),
    [cameras, activeSiteFilter],
  );

  /** Alert type + severity + "only open" — shared with the GIS and geographic boards, so the
   *  same question is asked the same way. */
  const filters = useBoardFilters({
    recent,
    cards,
    // The cameras this board is CURRENTLY laying out — so the per-alert counts follow the
    // site filter rather than counting cameras that are not on screen.
    cameraIds: useMemo(() => scopedCameras.map((c) => c.id), [scopedCameras]),
    counters,
  });
  const filterPanel = useFilterColumn('alert-board', false);

  /**
   * What the PANEL shows, under the alert-type and severity filters.
   *
   * The board dims and the panel filters, which sounds inconsistent and is not: a dimmed pin
   * still tells the operator that camera exists and where it is, while a card for an alert
   * they have filtered out tells them nothing and pushes the ones they asked for off screen.
   *
   * Filtered per ALERT, so a camera carrying two kinds contributes only the matching one.
   *
   * Note this narrows the page the server already returned; it does not ask for more. "Load
   * more" still pages, so a narrow filter over a busy estate may need a second press to fill
   * the panel — better than silently fetching pages until something matches.
   */
  // Keyed on the hook's memoised callbacks, not `filters` itself — that object is new on
  // every render, and depending on it would rebuild the board layer (below) every frame.
  const { matches: alertMatches, isDimmed: alertDimmed } = filters;
  const visibleCards = useMemo(() => cards.filter(alertMatches), [cards, alertMatches]);
  const visibleTiles = useMemo(
    () => liveTiles.filter((t) => alertMatches(t.alert)),
    [liveTiles, alertMatches],
  );

  const isDimmed = useCallback(
    (cam: HexCamera): boolean => alertDimmed(cam.id),
    [alertDimmed],
  );

  /** How many cameras survive the filters — the number the pill reports back. */
  const matchCount = filters.matchCount;

  // One site is the local node's whole world — a list offering "All sites" and the only site
  // says nothing. The panel grows this section back when cloud gives it real sites.
  const siteGroup = useMemo<FilterGroup | null>(
    () =>
      mergeSites
        ? null
        : {
            label: 'Site',
            value: siteFilter,
            // Picking a site REBUILDS the board around it — the other sites' cameras are not
            // dimmed, they're not drawn. The result line says so.
            effect: 'hide',
            onChange: setSiteFilter,
            chips: [
              { key: ALL, label: 'All sites', count: cameras.length },
              ...siteOptions.map((s) => ({ key: s.id, label: s.name, count: s.count })),
            ],
          },
    [mergeSites, cameras.length, siteOptions, siteFilter],
  );

  // ── Layout ────────────────────────────────────────────────────────────────
  /** Bucket cameras into group columns; ungrouped cameras sort last. */
  const clustersOf = useCallback((cams: HexCamera[], panelId: string) => {
    const groups = new Map<string, HexCamera[]>();
    cams.forEach((cam) => {
      const gid = cam.groupId ?? UNGROUPED;
      const arr = groups.get(gid);
      if (arr) arr.push(cam);
      else groups.set(gid, [cam]);
    });
    return [...groups.entries()]
      .map(([gid, items]) => ({
        id: `${panelId}::${gid}`,
        ungrouped: gid === UNGROUPED,
        title: gid === UNGROUPED ? 'Ungrouped' : items[0].groupName || 'Group',
        items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) =>
        a.ungrouped !== b.ungrouped ? (a.ungrouped ? 1 : -1) : a.title.localeCompare(b.title),
      )
      .map(({ ungrouped: _ungrouped, ...cluster }) => cluster);
  }, []);

  const siteInputs: SiteInput<HexCamera>[] = useMemo(() => {
    // ONE site: every group shares a single background panel, with no site header above it.
    // Splitting a one-site board into a panel per top-level group made each group look like
    // a separate place and repeated its name twice — once as the panel, once as the group.
    if (mergeSites) {
      // Deliberately NAMELESS, even where the caller knows the site's name: on a one-site
      // board every hex on screen belongs to it, so printing it above them labels nothing —
      // it just spends a line of the canvas restating the page the operator is already on.
      // An empty name is also what tells layoutSites to reserve no header band at all.
      return [{ id: BOARD_PANEL, name: '', clusters: clustersOf(scopedCameras, BOARD_PANEL) }];
    }

    const sites = new Map<string, { id: string; name: string; cams: HexCamera[] }>();
    scopedCameras.forEach((cam) => {
      const sid = cam.siteId || UNGROUPED;
      const hit = sites.get(sid);
      if (hit) hit.cams.push(cam);
      else sites.set(sid, { id: sid, name: siteNameOf(cam), cams: [cam] });
    });

    return [...sites.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((site) => ({ id: site.id, name: site.name, clusters: clustersOf(site.cams, site.id) }));
  }, [mergeSites, scopedCameras, siteNameOf, clustersOf]);

  /**
   * The width the honeycomb flows into: the canvas element's own width, quantised so a
   * drag-resize doesn't re-run the layout on every pixel. `size.w` is 0 for the first frame
   * (before the ResizeObserver fires), which layoutSites reads as "not measured yet".
   */
  // Laid out for the zoom the board is SHOWN at, not for 100%: the board opens (and "Fit"
  // returns) at DEFAULT_ZOOM, and a layout built for 1:1 then filled only 69% of the canvas
  // — a wide empty band down the right, and fewer groups per row than the screen could hold,
  // so more rows to scroll through. Divided by the zoom, the rows run edge to edge exactly
  // at the default level.
  // Rounded DOWN, so the laid-out board never ends up fractionally wider than the canvas it
  // was measured against.
  const flowWidth = useMemo(
    () => (size.w > 0 ? Math.floor(size.w / DEFAULT_ZOOM / 16) * 16 : 0),
    [size.w],
  );

  /**
   * "All" — the whole board on one screen.
   *
   * Zooming the normal layout out to fit was not enough: that layout is built for the
   * canvas WIDTH at the default zoom, so it is as wide as the screen and as tall as the
   * estate. Shrunk until its height fitted, a big board ended up a narrow column in the
   * middle with empty bands down both sides. In overview the groups are instead re-flowed
   * into the width whose board has the SAME shape as the canvas, which is the one that can
   * be drawn largest — so the cameras fill the screen rather than a strip of it.
   */
  const [overview, setOverview] = useState(false);

  const layout = useMemo(() => {
    if (!overview || size.w <= 0 || size.h <= 0) {
      return layoutSites(siteInputs, { targetWidth: flowWidth });
    }
    // Try flow widths on a ~5% geometric ladder and keep the one drawn largest. The layout is
    // plain arithmetic over the cameras, so ~100 tries per site arrangement cost a few ms even
    // on a big estate — and it runs only on entering overview or a resize, never per frame. Scale is capped at 1: past that a board is being magnified, not fitted, and
    // among layouts within 1% of the best the widest (fewest rows) wins.
    const fits = (l: ReturnType<typeof layoutSites<HexCamera>>) =>
      Math.min(1, size.w / l.width, size.h / (l.height + TOP_INSET + BOTTOM_INSET));
    const tries: { l: ReturnType<typeof layoutSites<HexCamera>>; sc: number }[] = [];
    // A multi-site board also tries its panels side by side (see `siteColumns`) — stacked
    // full-width, three sites make a board three times taller than it is wide.
    const maxSiteCols = Math.min(siteInputs.length, 6);
    for (let siteColumns = 1; siteColumns <= maxSiteCols; siteColumns += 1) {
      for (let w = 320; w <= 24000; w = Math.ceil(w * 1.05)) {
        const l = layoutSites(siteInputs, { targetWidth: w, siteColumns });
        tries.push({ l, sc: fits(l) });
      }
    }
    const top = Math.max(...tries.map((t) => t.sc));
    return tries
      .filter((t) => t.sc >= top * 0.99)
      .reduce((a, b) => (b.l.width > a.l.width ? b : a)).l;
  }, [overview, siteInputs, flowWidth, size.w, size.h]);

  // Re-fit whenever the overview's layout changes (entering it, a resize, cameras arriving),
  // and return to the default zoom when it is left. A layout effect, so the board never
  // paints a frame at the old scale with the new arrangement. Runs after HexCanvas has
  // published its handle for the new content size, so fitAll sees the new layout.
  const wasOverviewRef = useRef(false);
  useLayoutEffect(() => {
    if (overview) canvasRef.current?.fitAll();
    else if (wasOverviewRef.current) canvasRef.current?.fitToView();
    wasOverviewRef.current = overview;
  }, [overview, layout]);

  const hexById = useMemo(() => {
    const m: Record<string, PositionedHex<HexCamera>> = {};
    layout.hexes.forEach((h) => (m[h.item.id] = h));
    return m;
  }, [layout]);

  /** Every group on the board in reading order, for "Jump to group". */
  const groupIndex = useMemo(
    () =>
      layout.sites.flatMap((site) =>
        site.clusters.map((c) => ({
          id: c.id,
          // On a multi-site board a group name alone is ambiguous ("Perimeter" at two sites).
          label: site.name ? `${site.name} · ${c.title}` : c.title,
          count: c.hexes.length,
          y: c.originY + TOP_INSET,
        })),
      ),
    [layout],
  );

  const selectedHex = selectedId ? hexById[selectedId] : undefined;
  const hoveredHex = hoveredId ? hexById[hoveredId] : undefined;
  const hoveredAlert = hoveredId ? latestAlertFor(hoveredId) : undefined;

  // Hex centres are layout coordinates; the board is drawn TOP_INSET lower on the canvas.
  const screenOf = (cx: number, cy: number) => ({ x: vp.tx + cx * vp.scale, y: vp.ty + (cy + TOP_INSET) * vp.scale });
  const hexHalfW = (HEX_W / 2) * vp.scale;

  const handleSelect = useCallback((hex: PositionedHex<HexCamera>) => {
    setSelectedId((cur) => (cur === hex.item.id ? null : hex.item.id));
  }, []);

  /**
   * Every alert the panel is showing, in the order it shows them, reduced to what a detail view
   * needs. The click copies this, so paging with the arrow keys stays stable while the record is
   * open even as the live feed moves on.
   */
  const alertSiblings = useMemo<ViewAlertTarget[]>(
    // `visibleCards`, not `cards`: this is what ← / → step through, and it is documented as
    // what the panel is SHOWING. Paging through alerts the operator has just filtered out
    // would be the arrow keys disagreeing with the list they were pressed in.
    () => visibleCards.map((c) => viewAlertTarget(c, nameById)),
    [visibleCards, nameById],
  );

  const handleViewAlert = useCallback(
    (alert: HexAlert) =>
      onViewAlert?.({
        ...viewAlertTarget(alert, nameById),
        siblings: alertSiblings,
        // A live tile's alert is also in `cards` (those hold every active alert), but if it
        // somehow isn't, -1 → 0 makes this a list of one rather than a broken cursor.
        index: Math.max(0, alertSiblings.findIndex((s) => !!alert.id && s.id === alert.id)),
      }),
    [onViewAlert, nameById, alertSiblings],
  );

  /** Active alerts across every camera on the board — the title bar's "N open". Summed from
   *  the counters rather than `cards`, which is a paginated slice. */
  const activeAlerts = useMemo(
    () => Object.values(counters).reduce((n, c) => n + (c?.total || 0), 0),
    [counters],
  );
  // "N live" is the panel's own live section, so the two can never disagree.
  const liveCount = blocked ? 0 : visibleTiles.length;
  const openCount = blocked ? 0 : activeAlerts;
  useEffect(() => {
    onStats?.({ open: openCount, live: liveCount });
  }, [onStats, openCount, liveCount]);

  // ── Render-once layers ─────────────────────────────────────────────────────
  // The page re-renders on EVERY viewport frame (it needs the viewport to place the preview
  // card and print the zoom level). Built inline, that re-rendered every hexagon on the board
  // and the whole alert panel — players included — 60 times a second while scrolling, which
  // is what made scrolling a large board stutter. Memoised, React sees the same elements and
  // skips them; only the transform and the overlays actually change per frame.
  const onHoverStart = useCallback((hex: PositionedHex<HexCamera>) => setHoveredId(hex.item.id), []);
  const onHoverEnd = useCallback(() => setHoveredId(null), []);
  const boardLayer = useMemo(
    () =>
      layout.sites.map((site) => (
        <HexSiteView
          key={site.id}
          site={site}
          selectedId={selectedId}
          colorStateFor={colorStateFor}
          counterFor={counterFor}
          liveFor={liveFor}
          recordingFor={recordingFor}
          isDimmed={isDimmed}
          onSelect={handleSelect}
          onHoverStart={onHoverStart}
          onHoverEnd={onHoverEnd}
        />
      )),
    [layout, selectedId, colorStateFor, counterFor, liveFor, recordingFor, isDimmed, handleSelect, onHoverStart, onHoverEnd],
  );

  const onTileSelect = useCallback((id: string) => setSelectedId(id), []);
  const alertPanel = useMemo(
    () => (
      <AlertTileStack
        liveTiles={visibleTiles}
        persistentCameraId={primaryCameraId}
        heldCameraIds={heldCameraIds}
        cards={visibleCards}
        nameById={nameById}
        counters={board.counters}
        PlayerComponent={PlayerComponent}
        onSelect={onTileSelect}
        onExpire={board.handleTileExpire}
        onContinue={board.handleTileContinue}
        onAcknowledge={api.acknowledgeAlert ? board.handleAcknowledge : undefined}
        onResolve={api.resolveAlert ? board.handleResolve : undefined}
        onViewAlert={onViewAlert ? handleViewAlert : undefined}
        onOpenRecordings={onOpenRecordings}
        loadPreview={api.loadPreview}
        hasMore={board.hasMore}
        loadingMore={board.loadingCards}
        onLoadMore={board.loadMoreCards}
      />
    ),
    [
      visibleTiles, primaryCameraId, heldCameraIds, visibleCards, nameById, board.counters,
      PlayerComponent, onTileSelect, board.handleTileExpire, board.handleTileContinue,
      api.acknowledgeAlert, api.resolveAlert, api.loadPreview, board.handleAcknowledge,
      board.handleResolve, onViewAlert, handleViewAlert, onOpenRecordings, board.hasMore,
      board.loadingCards, board.loadMoreCards,
    ],
  );

  const selCaption = selectedHex
    ? latestAlertFor(selectedHex.item.id)?.alertName ||
      latestAlertFor(selectedHex.item.id)?.eventType ||
      'live'
    : '';

  /** The filters only mean something once there are cameras to filter. */
  const showFilters = !blocked && !loading && cameras.length > 0;
  const groupCount = groupIndex.length;

  return (
    <div className={`flex h-full flex-col gap-3 ${className}`}>
      {/* No toolbar row: the page name and the view toggle are in the app's title bar, and
          everything this board used to say in its toolbar now floats over the canvas. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
        <BoardCard
          ref={wrapperRef}
          className="h-[45vh] min-h-[260px] flex-none lg:h-auto lg:min-h-0 lg:flex-1"
          panel={
            showFilters && filterPanel.open ? (
              <BoardFilterPanel
                filters={filters}
                site={siteGroup}
                onCollapse={filterPanel.toggle}
                countLabel={camsLabel(filters.activeCount > 0 ? matchCount : scopedCameras.length)}
                /* What the choices did, in cameras. The board can't say this itself: a dimmed
                   hexagon looks exactly like a camera with nothing happening on it. */
                matchCount={matchCount}
                totalCount={cameras.length}
              />
            ) : null
          }
        >
          {blocked ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Select a site to view its cameras.
            </div>
          ) : loading ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading cameras…</div>
          ) : layout.sites.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No cameras to display.</div>
          ) : (
            <HexCanvas
              ref={canvasRef}
              contentWidth={layout.width}
              contentHeight={layout.height + TOP_INSET + BOTTOM_INSET}
              fitMode="width"
              fitScale={DEFAULT_ZOOM}
              navigation="scroll"
              onViewportChange={onViewportChange}
            >
              <div className="absolute left-0" style={{ top: TOP_INSET, width: layout.width, height: layout.height }}>
                {boardLayer}
              </div>
            </HexCanvas>
          )}

          {/* Screen-space overlay layer (not scaled by the canvas transform) */}
          <div className="pointer-events-none absolute inset-0 z-20">
            {selectedHex &&
              (() => {
                const p = screenOf(selectedHex.cx, selectedHex.cy);
                return (
                  <ClickPreviewCard
                    cameraId={selectedHex.item.id}
                    name={selectedHex.item.name}
                    caption={selCaption}
                    live={liveFor(selectedHex.item)}
                    recording={recordingFor(selectedHex.item)}
                    counter={counterFor(selectedHex.item)}
                    anchorX={p.x}
                    anchorY={p.y}
                    hexHalfW={hexHalfW}
                    viewportW={size.w}
                    viewportH={size.h}
                    PlayerComponent={PlayerComponent}
                    onOpenRecordings={onOpenRecordings}
                    onClose={() => setSelectedId(null)}
                  />
                );
              })()}
            {hoveredHex && hoveredId !== selectedId &&
              (() => {
                const p = screenOf(hoveredHex.cx, hoveredHex.cy);
                return (
                  <HexAlertPopover
                    alert={hoveredAlert}
                    name={hoveredHex.item.name}
                    live={liveFor(hoveredHex.item)}
                    recording={recordingFor(hoveredHex.item)}
                    counter={counterFor(hoveredHex.item)}
                    anchorX={p.x}
                    anchorY={p.y}
                    hexHalfW={hexHalfW}
                    viewportW={size.w}
                    viewportH={size.h}
                    /* The cloud has no route to a node's image store, so its alerts carry no
                       snapshotUrl and the card has to blob-fetch the picture through the
                       tunnel — the same resolver the alert cards use. */
                    loadPreview={api.loadPreview}
                  />
                );
              })()}
          </div>

          {/* ─── Floating controls ───
               Left: the Filters pill (folded; open, it is the panel's header instead) and the
               identity chip — which board, and how big. "Honeycomb" alone is a shape, not a
               meaning, so the chip says what it clusters BY; on a cross-site board how many
               PLACES the cameras are in is the first thing an operator scanning the estate
               needs. Right: zoom, Fit, All and full screen in one group. right-5, not right-3:
               the board's scrollbar runs down the right edge. */}
          <BoardTopBar
            insetClass="left-3 right-5"
            left={
              <>
                {showFilters && !filterPanel.open && (
                  <BoardFilterPill
                    onToggle={filterPanel.toggle}
                    countLabel={camsLabel(filters.activeCount > 0 ? matchCount : scopedCameras.length)}
                    activeCount={filters.activeCount + (activeSiteFilter !== ALL ? 1 : 0)}
                  />
                )}
                <BoardIdentityChip
                  icon={RectangleGroupIcon}
                  title={mergeSites ? 'Honeycomb · by camera group' : 'Honeycomb · by site'}
                  titleText="Honeycomb view — every camera, clustered by group"
                  detail={
                    blocked ? 'No site selected' : loading ? 'Loading cameras…' : (
                      <>
                        {!mergeSites && siteOptions.length > 0 && (
                          <>
                            {siteOptions.length} site{siteOptions.length === 1 ? '' : 's'}
                            {' · '}
                          </>
                        )}
                        {cameras.length} camera{cameras.length === 1 ? '' : 's'}
                        {groupCount > 0 && ` · ${groupCount} group${groupCount === 1 ? '' : 's'}`}
                      </>
                    )
                  }
                />
                {/* Jump to group — a board of many groups runs well below the fold, and
                    scrolling to find one by eye is the slow way. Native <select>: keyboard
                    type-ahead on the group name comes for free. */}
                {!blocked && !loading && groupCount >= JUMP_MIN_GROUPS && (
                  <select
                    data-tour="alert-board-jump"
                    aria-label="Jump to group"
                    value=""
                    onChange={(e) => {
                      const g = groupIndex.find((x) => x.id === e.target.value);
                      if (g) canvasRef.current?.scrollToContentY(g.y - 8);
                    }}
                    className={`${BOARD_FLOAT} h-10 max-w-[220px] cursor-pointer px-2.5 text-xs text-foreground outline-none hover:bg-muted focus:ring-1 focus:ring-brand`}
                  >
                    <option value="" disabled>
                      Jump to group ({groupCount})
                    </option>
                    {groupIndex.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.label} · {g.count}
                      </option>
                    ))}
                  </select>
                )}
              </>
            }
            center={centerSlot}
            right={
              (onToggleFullscreen || (!blocked && layout.sites.length > 0)) && (
                <BoardToolGroup>
                  {!blocked && layout.sites.length > 0 && (
                    <>
                      {/* "Fit" alone was the only way to change scale, which made every other
                          zoom a mouse-wheel guess; the read-out is what turns that into a
                          control an operator can return to a known level with. */}
                      <BoardToolButton label="Zoom out" onClick={() => canvasRef.current?.zoomBy(1 / 1.2)}>
                        <MinusIcon className="h-4 w-4" />
                      </BoardToolButton>
                      <span className="flex w-12 items-center justify-center text-xs font-semibold tabular-nums text-foreground">
                        {Math.round(vp.scale * 100)}%
                      </span>
                      <BoardToolButton label="Zoom in" onClick={() => canvasRef.current?.zoomBy(1.2)}>
                        <PlusIcon className="h-4 w-4" />
                      </BoardToolButton>
                      <BoardToolButton
                        label="Fit"
                        title="Back to the default zoom, fitted to the width"
                        onClick={() => {
                          // Leaving overview re-flows the board; the layout effect then does the fit.
                          if (overview) setOverview(false);
                          else canvasRef.current?.fitToView();
                        }}
                      >
                        <ArrowsPointingOutIcon className="h-4 w-4" /> Fit
                      </BoardToolButton>
                      {/* The whole estate on one screen, however small that makes it — the
                          overview an operator wants when the question is "is anything red
                          anywhere". */}
                      <BoardToolButton
                        label="All"
                        title={overview ? 'Back to the normal layout' : 'Arrange every camera to fit on one screen'}
                        pressed={overview}
                        onClick={() => setOverview((o) => !o)}
                      >
                        <ArrowsPointingInIcon className="h-4 w-4" /> All
                      </BoardToolButton>
                    </>
                  )}
                  {onToggleFullscreen && (
                    <BoardFullscreenButton fullscreen={fullscreen} onToggle={onToggleFullscreen} />
                  )}
                </BoardToolGroup>
              )
            }
          />

          <BoardLegend marker="hexagon" />
        </BoardCard>

        {/* Hidden transport warmer — keeps the WebRTC ICE/DTLS transport negotiated
            while no live tiles exist, so the next alert renders without a cold handshake.
            Rendered off-screen with no overlay; it only holds a subscription. */}
        {warmupCameraId && (
          <div aria-hidden className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0">
            <PlayerComponent cameraId={warmupCameraId} label="warmup" className="h-px w-px" />
          </div>
        )}

        <aside data-tour="alert-board-alerts" className="flex min-h-0 flex-1 flex-col rounded-lg border border-border bg-card p-3 lg:w-[360px] lg:flex-none lg:p-4">
          {alertPanel}
        </aside>
      </div>
    </div>
  );
}
